/**
 * Selection Helper Utilities
 *
 * Shared functions for handling row/column selection logic with Base type filtering.
 * This ensures consistent behavior across GraphConfigControls and PlatinumGraphs components.
 */

/**
 * Get selected row labels from indices or label names, with Base type filtering
 *
 * When selectedItems is empty, defaults to all non-Base rows.
 * When selectedItems has values, handles both numeric indices ("0", "1") and
 * actual label names ("Substantive", "Brief").
 *
 * @param selectedItems - Array of string indices (e.g., ["0", "1"]) or label names
 * @param allRows - All available row labels
 * @param allRowTypes - Corresponding types for each row
 * @returns Array of selected row label strings
 */
export function getSelectedRowLabels(
  selectedItems: string[],
  allRows: string[],
  allRowTypes: string[]
): string[] {
  // When no selections, default to all non-Base items
  if (selectedItems.length === 0) {
    return allRows.filter((_, index) => {
      const type = allRowTypes[index] || ''
      return type.toLowerCase() !== 'base'
    })
  }

  // When selections exist, handle both indices and label names
  return selectedItems
    .map(item => {
      // If it's already a label name that exists in allRows, use it as-is
      if (allRows.includes(item)) {
        return item
      }
      // Otherwise, try to parse it as an index
      const idx = parseInt(item, 10)
      if (!isNaN(idx) && idx >= 0 && idx < allRows.length) {
        return allRows[idx]
      }
      return undefined
    })
    .filter((v): v is string => v !== undefined)
}

/**
 * Get selected column labels from indices or label names, with Base type filtering
 *
 * When selectedItems is empty, defaults to all non-Base columns.
 * When selectedItems has values, handles both numeric indices ("0", "1") and
 * actual label names ("summer2024", "winter2024").
 *
 * @param selectedItems - Array of string indices (e.g., ["0", "1"]) or label names
 * @param allColumns - All available column labels
 * @param allColumnTypes - Corresponding types for each column
 * @param options - Optional configuration
 * @param options.useParsedDataFormat - If true, adds +1 offset for parsedData.headers format (which has empty string at index 0)
 * @returns Array of selected column label strings
 */
export function getSelectedColumnLabels(
  selectedItems: string[],
  allColumns: string[],
  allColumnTypes: string[],
  options?: { useParsedDataFormat?: boolean }
): string[] {
  // When no selections, default to all non-Base items
  if (selectedItems.length === 0) {
    return allColumns.filter((_, index) => {
      const type = allColumnTypes[index] || ''
      return type.toLowerCase() !== 'base'
    })
  }

  // When selections exist, handle both indices and label names
  // For parsedData format, headers have empty string at index 0, so we need +1 offset
  const offset = options?.useParsedDataFormat ? 1 : 0
  return selectedItems
    .map(item => {
      // If it's already a label name that exists in allColumns, use it as-is
      if (allColumns.includes(item)) {
        return item
      }
      // Otherwise, try to parse it as an index
      const idx = parseInt(item, 10)
      if (!isNaN(idx)) {
        const actualIdx = idx + offset
        if (actualIdx >= 0 && actualIdx < allColumns.length) {
          return allColumns[actualIdx]
        }
      }
      return undefined
    })
    .filter((v): v is string => v !== undefined)
}

/**
 * Get selected column label from a single index (singular version)
 *
 * Converts an index string to a column name. If the input is already a name
 * (not numeric), returns it as-is for backwards compatibility.
 *
 * @param selectedIndex - Single string index (e.g., "0") or name (e.g., "Oct-25")
 * @param allColumns - All available column labels
 * @returns The column label string, or undefined if index is out of range
 */
export function getSelectedColumnLabel(
  selectedIndex: string | undefined,
  allColumns: string[]
): string | undefined {
  if (!selectedIndex) return undefined

  // If it's already a name (not a number), return as-is
  const asNumber = parseInt(selectedIndex, 10)
  if (isNaN(asNumber)) return selectedIndex

  // Convert index to name
  if (asNumber >= 0 && asNumber < allColumns.length) {
    return allColumns[asNumber]
  }

  return undefined
}

/**
 * Resolves a stored row/column selection to true POSITIONS in `allItems`,
 * rather than to labels (#1173 fault 1). GraphConfigControls used to match a
 * stored entry back onto `allRows`/`allColumns` by label via
 * `findIndex(row => row === selectedRow)`, which collapses duplicate labels
 * (a base row like "Cases WF" repeats once per brand group) onto the first
 * occurrence (cause A), and turned an unmatched label into the truthy string
 * "-1" that survived `.filter(v => v)` (cause C). Position is the fix: two
 * rows sharing a label are independently selectable, and an entry that
 * matches nothing is dropped rather than kept.
 *
 * Same precedence as getSelectedRowLabels/getSelectedColumnLabels — a literal
 * label match wins over parsing the entry as an index — so legacy name-based
 * storage keeps resolving exactly as it always has; only the return type
 * differs (a position instead of a label).
 *
 * @param selectedItems - Array of string indices (e.g., ["0", "2"]) or legacy label names
 * @param allItems - All available row or column labels, in table order
 * @returns Resolved positions in `allItems`; [] means "all" (nothing selected)
 */
export function getSelectedPositions(selectedItems: string[], allItems: string[]): number[] {
  if (selectedItems.length === 0) return []

  return selectedItems
    .map(item => {
      const labelIndex = allItems.indexOf(item)
      if (labelIndex >= 0) return labelIndex
      const idx = parseInt(item, 10)
      return !isNaN(idx) && idx >= 0 && idx < allItems.length ? idx : undefined
    })
    .filter((v): v is number => v !== undefined)
}

/**
 * The positions a chart plots on one axis (#1808): the stored selection
 * resolved to positions (getSelectedPositions), or every non-Base position
 * when nothing is stored — the positional twin of getSelectedRowLabels'
 * default. The chart engine filters by these, so two rows with the same
 * label are picked apart and a base row sharing a data row's label stays out.
 */
export function getChartRowPositions(
  selectedItems: string[],
  allItems: string[],
  allTypes: string[],
): number[] {
  if (selectedItems.length === 0) {
    return allItems
      .map((_, index) => index)
      .filter((index) => (allTypes[index] || '').toLowerCase() !== 'base')
  }
  // A legacy entry stored as a label plots every row with that label, as the
  // name filter did before #1808; an index entry picks its one row.
  const positions = new Set<number>()
  for (const item of selectedItems) {
    if (allItems.includes(item)) {
      allItems.forEach((label, index) => { if (label === item) positions.add(index) })
    } else {
      for (const p of getSelectedPositions([item], allItems)) positions.add(p)
    }
  }
  return [...positions]
}
